import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  confirm,
  Dialog,
  DialogContent,
  DialogPortal,
  Modal,
  Textarea,
  toast,
} from "@devhop/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  BanIcon,
  BarChart3Icon,
  CheckCircle2Icon,
  ClipboardIcon,
  ClockIcon,
  ExpandIcon,
  MapPinIcon,
  ShieldCheckIcon,
  UploadIcon,
  WrenchIcon,
  XCircleIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { AuditItem } from "@/modules/audit/presentation/api/client";
import { fetchAudit } from "@/modules/audit/presentation/api/client";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { usersApi } from "@/modules/users/presentation/api/client";
import { BanUserForm } from "@/modules/users/presentation/ui/BanUserForm";
import { isDevMode } from "@/shared/lib/config";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { formatPrice } from "@/shared/lib/format-price";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import { AppImage, resolveImageSrc } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { mechanicsApi } from "../api/client";
import {
  mechanicsKeys,
  useMechanicMutations,
  useMechanicQuery,
} from "../api/queries";

function mechanicCode(id: string): string {
  const short = id.replace(/-/g, "").slice(-6).toUpperCase();
  return `MEC-${short}`;
}

function maskIdCard(value: string | null): string {
  if (!value) return "—";
  const digits = value.replace(/\D/g, "");
  if (digits.length < 4) return value;
  const first = digits.slice(0, 4);
  const last = digits.slice(-2);
  return `${first}-****-**${last}`;
}

function formatLak(n: number | null): string {
  return formatPrice(n, "LAK", { locale: "lo-LA" });
}

const STATUS_LABELS: Record<string, string> = {
  pending_verification: "ລໍຖ້າກວດສອບ",
  active: "ໃຊ້ງານປົກກະຕິ",
  suspended: "ລະງັບ",
};

const ACTION_TYPE_LABELS: Record<string, string> = {
  "MECHANIC.VERIFIED": "ຢືນຢັນ",
  "MECHANIC.VERIFICATION_REJECTED": "ປະຕິເສດ",
  "MECHANIC.SUSPENDED": "ລະງັບ",
  "MECHANIC.UNSUSPENDED": "ຍົກເລີກລະງັບ",
  "USER.BAN": "ແບນ",
  "USER.UNBAN": "ຍົກເລີກການແບນ",
};

export function MechanicDetailPage() {
  const nav = useNavigate({ from: "/app/mechanics/$id" });
  const { id } = useParams({ from: "/app/mechanics/$id" });
  const [reason, setReason] = useState("");
  const [banModal, setBanModal] = useState(false);
  const [unbanSubmitting, setUnbanSubmitting] = useState(false);
  const [idCardPreviewUrl, setIdCardPreviewUrl] = useState<string | null>(null);
  const [idCardUploading, setIdCardUploading] = useState(false);
  const [fullscreenImageSrc, setFullscreenImageSrc] = useState<string | null>(
    null,
  );
  const idCardFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      if (idCardPreviewUrl) URL.revokeObjectURL(idCardPreviewUrl);
    };
  }, [idCardPreviewUrl]);

  const qc = useQueryClient();
  const { data: mechanic, ...queryResult } = useMechanicQuery(id ?? "");
  const mutations = useMechanicMutations(id ?? "");
  const canVerify = useActionPermission([Permissions.mechanics.verify]);
  const canSuspend = useActionPermission([Permissions.mechanics.suspend]);
  const canBan = useActionPermission([Permissions.users.ban]);

  const auditQuery = useQuery({
    queryKey: ["audit", "mechanic", id],
    queryFn: () =>
      fetchAudit({
        limit: 10,
        offset: 0,
        filters: [
          { field: "entityType", op: "eq" as const, value: "mechanic" },
          { field: "entityId", op: "eq" as const, value: id ?? "" },
        ],
        sort: [{ field: "occurredAt", dir: "desc" as const }],
      }),
    enabled: !!id,
  });
  const auditItems: AuditItem[] = auditQuery.data?.data ?? [];

  const displayName =
    mechanic?.displayName || mechanic?.userName || mechanic?.userId || "—";
  const statusLabel =
    mechanic?.status != null
      ? (STATUS_LABELS[mechanic.status] ?? mechanic.status)
      : "—";
  const isPending = mechanic?.status === "pending_verification";
  const isSuspended = mechanic?.status === "suspended";
  const isVerified = !!mechanic?.verifiedAt;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ຄິວລົງທະບຽນຊ່າງ", to: "/app/mechanics" },
            { label: displayName },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ລາຍລະອຽດຊ່າງ</h2>
            <p className="text-muted-foreground">
              {mechanic
                ? `${displayName} • ${mechanicCode(mechanic.id)} • ${statusLabel}`
                : "ກຳລັງໂຫຼດ..."}
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="default"
              onClick={() =>
                nav({
                  to: "/app/mechanics/$id/performance",
                  params: { id: id ?? "" },
                })
              }
            >
              <BarChart3Icon className="size-4" />
              ລາຍງານການປະຕິບັດງານ
            </Button>
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/mechanics" })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>

        <QueryState result={queryResult} isEmpty={!mechanic}>
          <div className="mt-6 grid gap-6">
            {/* Card 1: Identity & Profile Details — layout ตามภาพ: ซ้าย = บัตร+รูป, ขวา = ข้อมูลโปรไฟล์ */}
            <Card className="gap-0 overflow-hidden rounded-xl border border-border bg-card p-0">
              <CardHeader className="!py-4 flex flex-row items-center justify-between border-b bg-muted/30">
                <CardTitle className="flex items-center gap-2 font-heading text-lg">
                  <ShieldCheckIcon className="size-5 text-scondary" />
                  ຂໍ້ມູນຕົວຕົນ ແລະ ໂປຣໄຟລ໌
                </CardTitle>
                {mechanic?.profileCreatedAt && (
                  <span className="font-normal text-muted-foreground text-xs">
                    ແກ້ໄຂຄັ້ງຫຼ້າ: {formatDateTimeLocal(mechanic.profileCreatedAt)}
                  </span>
                )}
              </CardHeader>
              <CardContent className="grid gap-6 p-4 md:grid-cols-2">
                {/* คอลัมน์ซ้าย: ເລກບັດ + ป้ายสถานะ + บล็อกເບິ່ງຮູບ */}
                <div className="flex flex-col gap-4">
                  <div>
                    <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                      ເລກບັດປະຈຳຕົວ
                    </span>
                    <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                      <p className="font-mono text-sm">
                        {mechanic ? maskIdCard(mechanic.idCardNo) : "—"}
                      </p>
                      {isVerified ? (
                        <Badge variant="default" className="gap-1">
                          <CheckCircle2Icon className="size-3" />
                          ຢືນຢັນແລ້ວ
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="gap-1">
                          <XCircleIcon className="size-3" />
                          ຍັງບໍ່ຢືນຢັນ
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex min-h-[200px] flex-col items-center justify-center rounded-lg border border-muted-foreground/30 border-dashed bg-muted/20 p-8">
                    <input
                      ref={idCardFileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      aria-hidden
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file || !id) return;
                        if (idCardPreviewUrl)
                          URL.revokeObjectURL(idCardPreviewUrl);
                        const url = URL.createObjectURL(file);
                        setIdCardPreviewUrl(url);
                        e.target.value = "";
                        setIdCardUploading(true);
                        try {
                          const key = await uploadImageToKey(
                            `uploads/mechanics/${id}`,
                            file,
                          );
                          await mechanicsApi.updateIdCardImage(id, key);
                          await qc.refetchQueries({
                            queryKey: mechanicsKeys.detail(id),
                          });
                          toast.success("ອັບໂຫຼດຮູບບັດສຳເລັດ");
                          setIdCardPreviewUrl(null);
                        } catch {
                          toast.error("ອັບໂຫຼດຮູບບໍ່ສຳເລັດ");
                          setIdCardPreviewUrl(null);
                        } finally {
                          setIdCardUploading(false);
                        }
                      }}
                    />
                    {idCardUploading ? (
                      <div className="flex flex-col items-center gap-2 py-8">
                        <div className="size-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                        <p className="text-muted-foreground text-sm">
                          ກຳລັງອັບໂຫຼດ...
                        </p>
                      </div>
                    ) : idCardPreviewUrl ? (
                      <>
                        <div className="group relative">
                          <img
                            src={idCardPreviewUrl}
                            alt="ຮູບບັດປະຈຳຕົວ (ຈຳລອງ)"
                            className="max-h-[240px] max-w-full rounded object-contain"
                          />
                          <div
                            className="absolute inset-0 flex items-center justify-center rounded bg-black/40 opacity-0 transition-opacity group-hover:opacity-100"
                            aria-hidden
                          >
                            <Button
                              variant="secondary"
                              size="sm"
                              className="gap-2 shadow-md"
                              onClick={() =>
                                setFullscreenImageSrc(idCardPreviewUrl)
                              }
                            >
                              <ExpandIcon className="size-4" />
                              ເບິ່ງຮູບເຕັມ
                            </Button>
                          </div>
                        </div>
                        {isDevMode && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="mt-2 text-muted-foreground"
                            onClick={() => {
                              if (idCardPreviewUrl) {
                                URL.revokeObjectURL(idCardPreviewUrl);
                                setIdCardPreviewUrl(null);
                              }
                            }}
                          >
                            ລຶບຮູບຈຳລອງ
                          </Button>
                        )}
                      </>
                    ) : mechanic?.idCardImageKey ? (
                      <>
                        <div className="group relative">
                          <AppImage
                            src={mechanic.idCardImageKey}
                            alt="ຮູບບັດປະຈຳຕົວ"
                            className="max-h-[240px] w-full"
                            fit="contain"
                          />
                          <div
                            className="absolute inset-0 flex items-center justify-center rounded bg-black/40 opacity-0 transition-opacity group-hover:opacity-100"
                            aria-hidden
                          >
                            <Button
                              variant="secondary"
                              size="sm"
                              className="gap-2 shadow-md"
                              onClick={() => {
                                const url = resolveImageSrc(
                                  mechanic.idCardImageKey,
                                );
                                if (url) setFullscreenImageSrc(url);
                              }}
                            >
                              <ExpandIcon className="size-4" />
                              ເບິ່ງຮູບເຕັມ
                            </Button>
                          </div>
                        </div>
                        {isDevMode && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2 border-amber-500 text-amber-600 hover:bg-amber-500/10 hover:text-amber-700"
                            onClick={() => idCardFileInputRef.current?.click()}
                          >
                            <UploadIcon className="size-4" />
                            ອັບໂຫຼດຮູບ (Dev)
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <ClipboardIcon className="size-12 text-muted-foreground" />
                        <p className="mt-2 text-muted-foreground text-sm">
                          ເບິ່ງຮູບບັດປະຈຳຕົວເຕັມ
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          disabled
                        >
                          ເບິ່ງຮູບ
                        </Button>
                        {isDevMode && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2 border-amber-500 text-amber-600 hover:bg-amber-500/10 hover:text-amber-700"
                            disabled={idCardUploading}
                            onClick={() => idCardFileInputRef.current?.click()}
                          >
                            <UploadIcon className="size-4" />
                            ອັບໂຫຼດຮູບ (Dev)
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {/* คอลัมน์ขวา: ຊື່, ເບີໂທ, ບົດແນະນຳ, ເມືອງ, ລັດສະໝີ */}
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ຊື່ທີ່ໃຊ້ສະແດງ
                      </span>
                      <p className="mt-1">{mechanic?.displayName ?? "—"}</p>
                    </div>
                    <div>
                      <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ເບີໂທ
                      </span>
                      <p className="mt-1">{mechanic?.userPhone ?? "—"}</p>
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                      ບົດແນະນຳຕົວ
                    </span>
                    <p className="mt-1 whitespace-pre-wrap rounded-md bg-muted p-2 text-sm">
                      {mechanic?.bio ?? "—"}
                    </p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="flex items-center gap-2">
                      <MapPinIcon className="size-4 shrink-0 text-muted-foreground" />
                      <div>
                        <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                          ເມືອງບໍລິການ
                        </span>
                        <p className="mt-0.5">
                          {mechanic?.serviceAreaCity ?? "—"}
                        </p>
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        ລັດສະໝີບໍລິການ
                      </span>
                      <p className="mt-1">
                        {mechanic?.serviceRadiusKm != null
                          ? `${mechanic.serviceRadiusKm} km`
                          : "—"}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 2: Mechanic Services */}
            <Card className="gap-0 overflow-hidden rounded-xl border border-border bg-card p-0">
              <CardHeader className="!py-4 border-b bg-muted/30">
                <CardTitle className="flex items-center gap-2 font-heading text-lg">
                  <WrenchIcon className="size-5 text-scondary" />
                  ບໍລິການຊ່າງ
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/20">
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                          ປະເພດບໍລິການ
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                          ລາຄາພື້ນຖານ
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                          ສະຖານະ
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(mechanic?.services?.length ?? 0) > 0 ? (
                        mechanic?.services?.map((s) => (
                          <tr key={s.id} className="border-b">
                            <td className="px-4 py-3">
                              <div className="font-medium">{s.title}</div>
                              <div className="text-muted-foreground text-xs">
                                {s.serviceKey}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {formatLak(s.basePrice)} ({s.currency})
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={s.isActive ? "default" : "muted"}>
                                {s.isActive ? "ໃຊ້ງານ" : "ປິດ"}
                              </Badge>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-4 py-6 text-center text-muted-foreground"
                          >
                            ບໍ່ມີລາຍການບໍລິການ
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Card 3: Account Security & Status */}
            <Card className="gap-0 overflow-hidden rounded-xl border border-border bg-card p-0">
              <CardHeader className="!py-4 border-b bg-muted/30">
                <CardTitle className="flex items-center gap-2 font-heading text-lg">
                  <ShieldCheckIcon className="size-5 text-scondary" />
                  ຄວາມປອດໄພບັນຊີ ແລະ ສະຖານະ
                </CardTitle>
              </CardHeader>
              <CardContent className="grid items-end gap-4 p-4 md:grid-cols-2">
                <div className="space-y-4">
                  <div>
                    <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                      ປ່ຽນສະຖານະບັນຊີ
                    </p>
                    <p className="mt-1 font-medium">
                      ສະຖານະລົງທະບຽນ:{" "}
                      <span
                        className={
                          isPending ? "text-[#F26522]" : "text-muted-foreground"
                        }
                      >
                        {isPending ? "ລໍຖ້າກວດສອບຕົວຕົນ" : statusLabel}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label
                      htmlFor="mechanic-reason"
                      className="font-medium text-muted-foreground text-xs uppercase tracking-wide"
                    >
                      ເຫດຜົນ (ໝາຍເຫດພາຍໃນ)
                    </label>
                    <Textarea
                      id="mechanic-reason"
                      placeholder="ໃສ່ເຫດຜົນການປ່ຽນສະຖານະ ຫຼື ການລະງັບ..."
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="mt-1 min-h-[80px]"
                      rows={3}
                    />
                  </div>
                  {isPending && canVerify && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        onClick={() =>
                          mutations.reject.mutate(
                            { reason: reason.trim() || undefined },
                            { onSuccess: () => setReason("") },
                          )
                        }
                        disabled={mutations.reject.isPending}
                      >
                        <XCircleIcon className="size-4" />
                        ປະຕິເສດຄຳຮ້ອງ
                      </Button>
                      <Button
                        onClick={() => mutations.verify.mutate(undefined)}
                        disabled={mutations.verify.isPending}
                      >
                        <CheckCircle2Icon className="size-4" />
                        ຢືນຢັນຕົວຕົນ
                      </Button>
                    </div>
                  )}
                </div>

                <div className="">
                  <Alert
                    variant="destructive"
                    className="block bg-destructive/10"
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <BanIcon className="size-4" />
                      <AlertTitle className="font-bold text-destructive-foreground">
                        ການກະທຳຂອງ Admin
                      </AlertTitle>
                    </div>
                    <AlertDescription className="text-destructive-foreground">
                      ການກະທໍາເຫຼົ່ານີ້ບໍ່ສາມາດກັບຄືນໄດ້ໃນເຊສຊັນປັດຈຸບັນ ແລະ
                      ຈະຖືກບັນທຶກໃນປະຫວັດການກວດກາ.
                    </AlertDescription>
                    <div className="mt-4 flex w-full flex-wrap items-center gap-2">
                      {canSuspend &&
                        (isSuspended ? (
                          <Button
                            variant="outline"
                            className="w-full flex-1"
                            onClick={() =>
                              mutations.unsuspend.mutate(
                                { reason: reason.trim() || undefined },
                                { onSuccess: () => setReason("") },
                              )
                            }
                            disabled={mutations.unsuspend.isPending}
                          >
                            ຍົກເລີກລະງັບ
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            className="w-full flex-1"
                            onClick={async () => {
                              const ok = await confirm({
                                title: "ລະງັບບັນຊີຊ່າງ",
                                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລະງັບບັນຊີຊ່າງນີ້?",
                              });
                              if (ok)
                                mutations.suspend.mutate({
                                  reason: reason.trim() || undefined,
                                });
                            }}
                            disabled={mutations.suspend.isPending}
                          >
                            ລະງັບບັນຊີ
                          </Button>
                        ))}
                      {canBan && !mechanic?.userBanned && (
                        <Button
                          className="w-full flex-1"
                          onClick={() => setBanModal(true)}
                        >
                          <BanIcon className="size-4" />
                          ແບນບັນຊີ
                        </Button>
                      )}
                      {canBan && mechanic?.userBanned && mechanic?.userId && (
                        <Button
                          variant="outline"
                          className="w-full flex-1"
                          disabled={unbanSubmitting}
                          onClick={async () => {
                            const ok = await confirm({
                              title: "ຍົກເລີກການແບນບັນຊີ",
                              description:
                                "ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການຍົກເລີກການແບນບັນຊີຜູ້ໃຊ້ນີ້? ຜູ້ໃຊ້ຈະສາມາດເຂົ້າສູ່ລະບົບໄດ້ອີກ.",
                            });
                            if (!ok || !mechanic?.userId) return;
                            setUnbanSubmitting(true);
                            try {
                              await usersApi.unban(mechanic.userId);
                              toast.success("ຍົກເລີກການແບນສຳເລັດ");
                              if (id) {
                                qc.invalidateQueries({
                                  queryKey: mechanicsKeys.detail(id),
                                });
                              }
                              qc.invalidateQueries({
                                queryKey: mechanicsKeys.all,
                              });
                            } catch {
                              toast.error("ບໍ່ສາມາດຍົກເລີກການແບນໄດ້");
                            } finally {
                              setUnbanSubmitting(false);
                            }
                          }}
                        >
                          ຍົກເລີກການແບນບັນຊີ
                        </Button>
                      )}
                    </div>
                  </Alert>
                </div>
              </CardContent>
            </Card>

            {/* Card 4: History & Audit Log */}
            <Card className="gap-0 overflow-hidden rounded-xl border border-border bg-card p-0">
              <CardHeader className="!py-4 border-b bg-muted/30">
                <CardTitle className="flex items-center gap-2 font-heading text-lg">
                  <ClockIcon className="size-5 text-secondary" />
                  ປະຫວັດ ແລະ ບັນທຶກການກວດກາ
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/20">
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                          ວັນທີ ແລະ ເວລາ
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                          ປະເພດ
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                          ການກະທໍາ / ເຫດການ
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                          ຜູ້ປະຕິບັດ
                        </th>
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                          ລາຍລະອຽດ
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditItems.length > 0 ? (
                        auditItems.map((item) => (
                          <tr key={item.id} className="border-b">
                            <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                              {formatDateTimeLocal(item.occurredAt)}
                            </td>
                            <td className="px-4 py-3">
                              <Badge
                                variant="secondary"
                                className="text-[10px]"
                              >
                                {ACTION_TYPE_LABELS[item.action] ?? item.action}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 font-medium">
                              {item.action}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              {item.actorRole ?? item.actorId ?? "—"}
                            </td>
                            <td className="max-w-[200px] truncate px-4 py-3 text-muted-foreground text-xs">
                              {typeof item.meta?.note === "string"
                                ? item.meta.note
                                : typeof item.meta?.reason === "string"
                                  ? item.meta.reason
                                  : (item.result ?? "—")}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-4 py-6 text-center text-muted-foreground"
                          >
                            {auditQuery.isLoading ? "ກຳລັງໂຫຼດ..." : "ບໍ່ມີບັນທຶກ"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Ban Modal */}
          {mechanic?.userId && (
            <Modal
              open={banModal}
              onOpenChange={(open) => setBanModal(open)}
              title="ແບນບັນຊີທົ່ວແຜງ"
              description="ຜູ້ໃຊ້ຈະບໍ່ສາມາດເຂົ້າສູ່ລະບົບໄດ້. ການກະທໍານີ້ຈະຖືກບັນທຶກໃນປະຫວັດການກວດກາ."
              size="sm"
            >
              <BanUserForm
                submitting={false}
                onSubmit={async (vals) => {
                  try {
                    await usersApi.ban(mechanic.userId, {
                      reason: vals.reason,
                      expires: vals.expires,
                    });
                    toast.success("ແບນບັນຊີສຳເລັດ");
                    setBanModal(false);
                    if (id)
                      qc.invalidateQueries({
                        queryKey: mechanicsKeys.detail(id),
                      });
                    qc.invalidateQueries({ queryKey: mechanicsKeys.all });
                  } catch {
                    toast.error("ບໍ່ສາມາດແບນໄດ້");
                  }
                }}
              />
            </Modal>
          )}

          {/* Fullscreen image dialog */}
          <Dialog
            open={!!fullscreenImageSrc}
            onOpenChange={(open) => {
              if (!open) setFullscreenImageSrc(null);
            }}
          >
            <DialogPortal>
              <DialogContent
                onClick={(e) => setFullscreenImageSrc(null)}
                className="!max-w-[90vw] fixed flex size-full max-h-[90vh] items-center justify-center border-0 bg-transparent p-4 shadow-none"
                showCloseButton
                aria-label="ຮູບເຕັມຂະໜາດ"
              >
                {fullscreenImageSrc && (
                  <img
                    src={fullscreenImageSrc}
                    alt="ຮູບບັດປະຈຳຕົວເຕັມຂະໜາດ"
                    className="size-full max-h-full max-w-full object-contain"
                  />
                )}
              </DialogContent>
            </DialogPortal>
          </Dialog>
        </QueryState>
      </Main>
    </>
  );
}
